/**
 * 石川県の背景ツアー「石川めぐり」コンテンツ。
 *
 * 軸は一日の時間。朝の千里浜から、冬の夕暮れの兼六園へ。
 *
 * 令和6年能登半島地震のあとの扱い。
 *  公式で現況を確かめたうえで、次のものは景色から外した。
 *   - 見附島 … 石川県観光連盟公式が「地震の影響により南東側半分が崩壊し、
 *     その姿が大きく変わりました」と書いている。軍艦の形はもう無い。
 *   - 輪島朝市 … 公式のスポット名が「出張輪島朝市ｉｎワイプラザ輪島店」。
 *     朝市通りに露店が並ぶ景色は今は無い。
 *   - 和倉温泉 … 屋台村は終了し、現在は仮設の商店街。
 *     大型旅館が並ぶ温泉街の絵は現況と合わない。
 *   - 總持寺祖院 … 修復工事が続き、立ち入り禁止のエリアが残る。
 *   - 曽々木海岸の窓岩・禄剛埼灯台 … 公式に地震の影響の記述が無く、
 *     現況を確認できなかったので今回は見送った。
 *  残した白米千枚田は、棚田そのものは景観として紹介が続いている。
 *  ただし冬のイルミネーション「あぜのきらめき」は令和7年度は開催されていないので、
 *  光の演出は描かせない。
 *
 * 金沢21世紀美術館の作品は現代作家の著作物なので扱わない。
 * 鶴仙渓のあやとりはしも華道家のデザインと公式に明記されているので、
 * 総ヒノキ造りのこおろぎ橋のほうを採った。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - ひがし茶屋街の「二階建て」「紅殻格子」は石川県・金沢市の公式に記述が無い。
 *    公式で確認できたのは「出格子」と「石畳」だけ。この二つで書く。
 *  - 兼六園の特別名勝の指定年、徽軫灯籠と霞ヶ池の寸法は公式で確認できなかった。
 *  - 妙成寺は「北陸随一の五重塔」（羽咋市公式）。「日本一」「最古」とは書かない。
 *  - 金沢城の菱櫓・五十間長屋は2001年の復元、石川門は天明8年（1788）再建の現存建築。
 *    この二つを混ぜない。
 *  - 輪島朝市の「千年以上」「日本三大朝市」は公式で確認できなかった。
 */

import type { StageTour } from '../types';

export const ishikawaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'chirihama',
      name: '千里浜なぎさドライブウェイ',
      area: '羽咋市・朝',
      image: '/assets/scenery/ishikawa/scenes/01_chirihama.webp',
      hotspots: [
        {
          id: 'chirihama-8km',
          x: 0.5,
          y: 0.6,
          title: '全長約8キロ',
          body: '千里浜なぎさドライブウェイは全長約8キロメートルの天然の砂浜ドライブウェイです。',
          source: 'https://www.city.hakui.lg.jp/kankou/kankoushisetsu/3168.html',
          mapQuery: '千里浜なぎさドライブウェイ',
        },
        {
          id: 'chirihama-0_2mm',
          x: 0.24,
          y: 0.44,
          title: '砂粒は約0.2ミリ',
          body: '千里浜の砂は一粒ひとつぶが約0.2ミリメートル。同じ大きさの細かい砂が水を含み、固く引き締まることから走行が可能になります。',
          source: 'https://www.city.hakui.lg.jp/kankou/kankoushisetsu/3168.html',
          mapQuery: '千里浜 砂',
        },
        {
          id: 'chirihama-kisei',
          x: 0.76,
          y: 0.34,
          title: '波が高いと通れない',
          body: '国内唯一、世界でも珍しい、波打ち際を自動車で走ることができる場所です。波が高いなど安全に走行できないと判断された場合は、管理者の石川県が通行規制を行い、車で走ることができません。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5818.html',
          mapQuery: '羽咋市 千里浜',
        },
      ],
    },
    {
      id: 'shiroyone-senmaida',
      name: '白米千枚田',
      area: '輪島市・午前',
      image: '/assets/scenery/ishikawa/scenes/02_shiroyone_senmaida.webp',
      hotspots: [
        {
          id: 'senmaida-1004',
          x: 0.5,
          y: 0.5,
          title: '全部で1004枚',
          body: '急斜面に幾重にも段になり海へと広がる田んぼは、その数が全部で1004枚あります。世界農業遺産に登録された「能登の里山里海」を代表する景観です。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5573.html',
          mapQuery: '白米千枚田',
        },
        {
          id: 'senmaida-meisho',
          x: 0.24,
          y: 0.64,
          title: '国指定名勝',
          body: '平成13年1月29日に国の名勝に指定されました。面積は1万8136平方メートル、水田一面あたりの平均面積は約20平方メートルと狭小です。',
          source: 'https://www.pref.ishikawa.lg.jp/kyoiku/bunkazai/siseki/2-4.html',
          mapQuery: '白米千枚田 名勝',
        },
        {
          id: 'senmaida-50m',
          x: 0.76,
          y: 0.32,
          title: '高低差約50メートル',
          body: '高低差約50メートルの急斜地に小区画の水田が耕作されています。1684年の大規模地すべりで失われた農地が明治期に再開拓されました。景観・地すべり地形・耕地開拓史・国土環境保全の各視点から高い評価を得ています。',
          source: 'https://www.pref.ishikawa.lg.jp/kyoiku/bunkazai/siseki/2-4.html',
          mapQuery: '輪島市 白米町',
        },
      ],
    },
    {
      id: 'gammon',
      name: '能登金剛 巌門',
      area: '志賀町・午前',
      image: '/assets/scenery/ishikawa/scenes/03_gammon.webp',
      hotspots: [
        {
          id: 'gammon-sunpo',
          x: 0.5,
          y: 0.52,
          title: '幅6メートル、奥行き60メートル',
          body: '海に突き出た岩盤にある、浸食によってぽっかりとあいた洞門は、幅6メートル、高さ15メートル、奥行き60メートルもあります。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5174.html',
          mapQuery: '巌門',
        },
        {
          id: 'gammon-oimatsu',
          x: 0.24,
          y: 0.34,
          title: '洞門の上の老松',
          body: '洞門の上には老松が生い茂っています。巌門は能登金剛を代表する存在です。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5174.html',
          mapQuery: '能登金剛 巌門',
        },
        {
          id: 'gammon-kongo',
          x: 0.76,
          y: 0.62,
          title: '福浦港から関野鼻まで',
          body: '能登金剛は能登半島国定公園を代表する景観の一つで、福浦港から関野鼻までの海岸線をさします。険しい断崖と荒波が作り出した奇岩が続いています。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_6100.html',
          mapQuery: '能登金剛',
        },
      ],
    },
    {
      id: 'hakusan-gozengamine',
      name: '白山 御前峰',
      area: '白山市・夏の昼',
      image: '/assets/scenery/ishikawa/scenes/04_hakusan_gozengamine.webp',
      hotspots: [
        {
          id: 'hakusan-2702',
          x: 0.5,
          y: 0.4,
          title: '最高峰の御前峰',
          body: '白山の最高峰は御前峰で、標高2,702メートル。白山国立公園は、御前峰・大汝峰・剣ヶ峰の3峰と周囲の山々を中心に、およそ南北40キロ、東西30キロにわたって広がっています。',
          source: 'https://www.env.go.jp/park/hakusan/point/index.html',
          mapQuery: '白山 御前峰',
        },
        {
          id: 'hakusan-ohanabatake',
          x: 0.24,
          y: 0.58,
          title: '7月から8月のお花畑',
          body: '7月から8月の夏山シーズンに高山植物が一斉に咲き誇り、見事なお花畑が広がります。ハクサンコザクラ、ハクサンフウロ、ゴゼンタチバナなど、白山や御前峰にちなんだ植物が数多く生育しています。',
          source: 'https://www.env.go.jp/park/hakusan/point/index.html',
          mapQuery: '白山 高山植物',
        },
        {
          id: 'hakusan-250',
          x: 0.76,
          y: 0.62,
          title: '250種の高山植物',
          body: '標高1,600メートル以下の山麓にはブナを中心とした自然林が広がり、高山・亜高山帯には250種もの高山植物が見られます。',
          source: 'https://www.env.go.jp/park/hakusan/point/index.html',
          mapQuery: '白山国立公園',
        },
      ],
    },
    {
      id: 'ishikawamon',
      name: '金沢城 石川門',
      area: '金沢市・昼過ぎ',
      image: '/assets/scenery/ishikawa/scenes/05_ishikawamon.webp',
      hotspots: [
        {
          id: 'ishikawamon-jubun',
          x: 0.5,
          y: 0.48,
          title: '昭和25年に重要文化財',
          body: '石川門は昭和25年（1950年）8月29日に重要文化財に指定されました。',
          source: 'https://shiro-niwa.pref.ishikawa.lg.jp/kanazawa-castle/explore/ishikawamon.php',
          mapQuery: '金沢城 石川門',
        },
        {
          id: 'ishikawamon-tenmei',
          x: 0.24,
          y: 0.62,
          title: '天明8年の再建',
          body: '天明8年（1788年）に再建され、現在まで残っています。',
          source: 'https://shiro-niwa.pref.ishikawa.lg.jp/kanazawa-castle/explore/ishikawamon.php',
          mapQuery: '金沢城公園',
        },
        {
          id: 'ishikawamon-namako',
          x: 0.76,
          y: 0.34,
          title: '白漆喰と海鼠壁と鉛瓦',
          body: '白漆喰塗に仕上げて腰回りに海鼠壁を配し、屋根はいずれも鉛瓦葺きです。',
          source: 'https://shiro-niwa.pref.ishikawa.lg.jp/kanazawa-castle/explore/ishikawamon.php',
          mapQuery: '金沢城 海鼠壁',
        },
      ],
    },
    {
      id: 'higashi-chayagai',
      name: 'ひがし茶屋街',
      area: '金沢市・午後',
      image: '/assets/scenery/ishikawa/scenes/06_higashi_chayagai.webp',
      hotspots: [
        {
          id: 'higashi-judenken',
          x: 0.5,
          y: 0.5,
          title: '重要伝統的建造物群保存地区',
          body: '美しい出格子と石畳が続く古い街並みは、国の重要伝統的建造物群保存地区に選定されています。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_6320.html',
          mapQuery: 'ひがし茶屋街',
        },
        {
          id: 'higashi-degoshi',
          x: 0.24,
          y: 0.38,
          title: '江戸時代のお茶屋が残る',
          body: '国指定重要文化財の「志摩」は、江戸時代そのままの建物が残るお茶屋です。昔ながらのお茶屋を見学できます。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_6320.html',
          mapQuery: '金沢市 東山',
        },
        {
          id: 'higashi-chaya',
          x: 0.76,
          y: 0.62,
          title: '金箔の茶室',
          body: '金沢で一番大きなお茶屋建築があり、金箔で織られた畳の茶室や輪島塗の階段が残されています。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_6320.html',
          mapQuery: 'ひがし茶屋街 茶屋',
        },
      ],
    },
    {
      id: 'myojoji',
      name: '妙成寺 五重塔',
      area: '羽咋市・午後',
      image: '/assets/scenery/ishikawa/scenes/07_myojoji.webp',
      hotspots: [
        {
          id: 'myojoji-1618',
          x: 0.5,
          y: 0.44,
          title: '元和4年の建立',
          body: '五重塔は元和4年（1618年）に大工の坂上越後守嘉紹が造りました。方三間、屋根は柿葺で、重要文化財の指定は昭和25年8月29日です。',
          source: 'https://www.pref.ishikawa.lg.jp/kyoiku/bunkazai/kenzoubutu/3.html',
          mapQuery: '妙成寺 五重塔',
        },
        {
          id: 'myojoji-34m',
          x: 0.24,
          y: 0.6,
          title: '総高34.18メートル',
          body: '総高は相輪を含めて34.18メートル。本堂は慶長19年（1614年）、祖師堂は寛永元年（1624年）の建立で、いずれも桁行5間、梁行5間の入母屋造り、柿葺です。',
          source: 'https://www.pref.ishikawa.lg.jp/kyoiku/bunkazai/kenzoubutu/3.html',
          mapQuery: '妙成寺',
        },
        {
          id: 'myojoji-hokuriku',
          x: 0.76,
          y: 0.32,
          title: '北陸随一とされる五重塔',
          body: '羽咋市公式は「北陸随一の五重塔は、海風に耐える頑丈な造りです」と紹介しています。境内の重要文化財の建造物は本堂、祖師堂、五重塔、経堂、書院、鐘楼、二王門、三光堂、三十番堂、庫裡の10棟。永仁2年（1294年）に開山しました。',
          source:
            'https://www.city.hakui.lg.jp/soshiki/sangyoukensetsubu/syoukoukankouka/12/1/2500.html',
          mapQuery: '羽咋市 妙成寺',
        },
      ],
    },
    {
      id: 'natadera',
      name: '那谷寺 奇岩遊仙境',
      area: '小松市・秋の夕方前',
      image: '/assets/scenery/ishikawa/scenes/08_natadera.webp',
      hotspots: [
        {
          id: 'natadera-meisho',
          x: 0.5,
          y: 0.5,
          title: '国の指定名勝',
          body: '境内には国の指定名勝で山水画のような「奇岩遊仙境」があります。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5964.html',
          mapQuery: '那谷寺 奇岩遊仙境',
        },
        {
          id: 'natadera-717',
          x: 0.24,
          y: 0.36,
          title: '717年に泰澄が開創',
          body: '那谷寺は717年に泰澄によって開創されました。中世末期は一向一揆で荒廃しましたが、江戸時代に加賀藩3代藩主の前田利常が復興しました。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5964.html',
          mapQuery: '那谷寺',
        },
        {
          id: 'natadera-jubun',
          x: 0.76,
          y: 0.64,
          title: '重要文化財の建物',
          body: '本堂、三重塔、護摩堂、鐘楼、書院および庫裡庭園が重要文化財です。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5964.html',
          mapQuery: '小松市 那谷寺',
        },
      ],
    },
    {
      id: 'koorogibashi',
      name: '鶴仙渓 こおろぎ橋',
      area: '加賀市・秋の夕',
      image: '/assets/scenery/ishikawa/scenes/09_koorogibashi.webp',
      hotspots: [
        {
          id: 'koorogi-hinoki',
          x: 0.5,
          y: 0.5,
          title: '総ヒノキ造り',
          body: '鶴仙渓に架かる総ヒノキ造りの風雅な橋です。四季を彩るその風情に、日本の情緒が感じられます。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5328.html',
          mapQuery: 'こおろぎ橋',
        },
        {
          id: 'koorogi-kakusenkei',
          x: 0.24,
          y: 0.62,
          title: '山中温泉を代表する名勝',
          body: '山中温泉を代表する名勝で、橋から眺める鶴仙渓は絶景です。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5328.html',
          mapQuery: '鶴仙渓',
        },
        {
          id: 'koorogi-shiki',
          x: 0.76,
          y: 0.32,
          title: '名前の由来',
          body: 'かつては行路が極めて危なかったので「行路危」と呼ばれたとされ、一説には秋の夜に鳴くコオロギに由来するともいわれています。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5328.html',
          mapQuery: '加賀市 山中温泉',
        },
      ],
    },
    {
      id: 'kenrokuen-yukitsuri',
      name: '兼六園 唐崎松の雪吊り',
      area: '金沢市・冬の夕暮れ',
      image: '/assets/scenery/ishikawa/scenes/10_kenrokuen_yukitsuri.webp',
      hotspots: [
        {
          id: 'kenrokuen-800',
          x: 0.5,
          y: 0.46,
          title: '芯柱5本、縄約800本',
          body: '唐崎松の雪吊りは、5本の芯柱が立てられ、総数約800本の縄で枝を吊ります。',
          source: 'https://www.pref.ishikawa.lg.jp/kisya/r6-1/documents/1028_10_kouenryokuti.pdf',
          mapQuery: '兼六園 唐崎松',
        },
        {
          id: 'kenrokuen-4000kg',
          x: 0.24,
          y: 0.62,
          title: 'わら縄約4,000キロ',
          body: '令和6年の作業では、園全体でリンゴ吊り52本、幹吊り59本、その他約700箇所に雪吊りを施し、わら縄は約4,000キログラム、径6ミリと8ミリの2種類を使いました。',
          source: 'https://www.pref.ishikawa.lg.jp/kisya/r6-1/documents/1028_10_kouenryokuti.pdf',
          mapQuery: '兼六園 雪吊り',
        },
        {
          id: 'kenrokuen-tokubetsu',
          x: 0.76,
          y: 0.3,
          title: '国の特別名勝',
          body: '兼六園は、水戸の偕楽園、岡山の後楽園とともに日本三名園と称され、国の特別名勝に指定されています。敷地は約11.4万平方メートルと広大です。',
          source: 'https://www.hot-ishikawa.jp/spot/detail_5286.html',
          mapQuery: '兼六園',
        },
      ],
    },
  ],
};
